
import { ChangeDetectorRef, ElementRef
       } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { SVG, Point, Matrix } from '@svgdotjs/svg.js';
import { FittingSidePosition, Utils } from 'core/utils';
import { Placeholder } from '../placeholder';

export interface TriggerIcon
{
  type: string;
  value: string;
}

export interface Region
{
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface Trigger
{
  icon: TriggerIcon;
  action: string;
}

export interface TriggerGroup
{
  triggers: Array<Trigger>;
  top: string;
  left: string;
  alignVertical: string;
  alignHorizontal: string;
  direction: string;
}

export abstract class OverlayComponent
{
  public nativeElement: Node | undefined;
  public targetRegion: Region | undefined;
  public triggersAreUseable: boolean = true;
  public placeholder: Placeholder | undefined;
  public triggerGroup: TriggerGroup | undefined;

  protected abstract triggersRef: ElementRef;
  protected triggerSide: FittingSidePosition;

  private sideCandidates: Array<FittingSidePosition>;
  private loadedSVGIcons: Array<string> = [];

  constructor(
      element: ElementRef,
      private cdr: ChangeDetectorRef,
      private mir: MatIconRegistry,
      private sanitizer: DomSanitizer)
  {
    this.nativeElement = element.nativeElement;
    this.resetSideCandidates();
    this.triggerSide = this.sideCandidates[0];
  }

  private resetSideCandidates(): void
  {
    this.sideCandidates = Utils.deepCopy(this.AVAILABLE_SIDES);
  }

  protected abstract get AVAILABLE_SIDES(): Array<FittingSidePosition>;
  protected abstract get FALLBACK_SIDE(): FittingSidePosition;

  public get currentTriggerSide(): FittingSidePosition
  {
    return this.triggerSide;
  }

  public initialize(
    placeholder: Placeholder): void
  {
    this.placeholder = placeholder;
    this.setTargetRegion();
    this.updateFittingSide();
  }

  public setTargetRegion(): void
  {
    if (! this.placeholder) {
      return;
    }

    this.targetRegion = this.calculateTargetRegion();

    if (Utils.ChangeDetection.isPaused) {
      this.cdr.detectChanges();
    }
  }

  protected calculateTargetRegion(): Region
  {
    const transform = (p: any, matrix: DOMMatrix) =>
      p.transform(matrix) as any;

    const wrapper = this.placeholder.wrapper;
    const wrapperCTM = DOMMatrix.fromMatrix(
        (<any>wrapper).parent().node.getScreenCTM());

    const x = wrapper.x() as number;
    const y = wrapper.y() as number;
    const w = wrapper.width() as number;
    const h = wrapper.height() as number;

    const rotation = (() => {
      const tl = transform(new Point(x, y), wrapperCTM);
      const tr = transform(new Point(x + w, y), wrapperCTM);
      return Utils.rotationToOrigin(tr.x - tl.x, tr.y - tl.y);
    })();

    const stableCTM = wrapperCTM.multiply(new DOMMatrix()
      .translate(wrapper.cx(), wrapper.cy())
      .rotate(-rotation)
      .translate(-wrapper.cx(), -wrapper.cy()));

    const topLeft = transform(new Point(x, y), stableCTM);
    const bottomRight = transform(new Point(x + w, y + h), stableCTM);

    const off = Utils.getTotalOffset((<any>this.nativeElement).offsetParent);

    return {
      x: topLeft.x - off.left,
      y: topLeft.y - off.top,
      width: bottomRight.x - topLeft.x,
      height: bottomRight.y - topLeft.y,
      rotation: rotation,
    };
  }

  public updateFittingSide(): void
  {
    this.updateTriggerGroup();

    setTimeout(() => {
      if (this.sideCandidates.length < 1) {
          this.triggersAreUseable = true;
          this.resetSideCandidates();

          this.triggerSide = this.FALLBACK_SIDE;
          this.updateTriggerGroup();

          return;
      }

      if (this.checkTriggersAreUseable()) {
        this.triggersAreUseable = true;
        this.resetSideCandidates();
        return;
      }

      this.triggersAreUseable = false;
      this.triggerSide = this.sideCandidates.shift();
      this.updateFittingSide();
    }, 0);
  }

  protected abstract updateTriggerGroup(): void;

  protected abstract checkTriggersAreUseable(): boolean;

  protected loadIconsIfRequired(triggers: Array<Trigger>): void
  {
    triggers.forEach(trigger => {
      if (trigger.icon.type === 'svg' &&
        this.loadedSVGIcons.indexOf(trigger.action) === -1) {
        this.mir.addSvgIcon(trigger.icon.value,
            this.sanitizer.bypassSecurityTrustResourceUrl(Utils.url(
                './assets/img/icons/overlays/' + trigger.icon.value + '.svg')));
        this.loadedSVGIcons.push(trigger.action);
      }
    });
  }

  public abstract triggerClicked(action: string): void;
}
